import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { GrowthAgentWorkflowBinding, GrowthAgentService } from './growth-agent.service';

@Component({
  standalone: true,
  selector: 'ain-growth-agent-workflow-bindings',
  imports: [
    FormsModule, AinPageComponent, InfoHintComponent, AinFormFieldComponent, TableModule, ButtonModule,
    SelectModule, InputTextModule, DialogModule
  ],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.agentWorkflow.title')" [description]="t('growth.agentWorkflow.description')">
      <ain-info-hint page-title-addon [titleKey]="'growth.agentWorkflow.help.title'" [descriptionKey]="'growth.agentWorkflow.help.description'"
        [bulletKeys]="['growth.agentWorkflow.help.bullets.noParallel','growth.agentWorkflow.help.bullets.fallback']" />

      <section class="card">
        <div class="toolbar">
          <button pButton type="button" [label]="t('common.add')" (click)="openDialog()"></button>
          <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load()"></button>
        </div>
        <p-table [value]="items()" [rows]="10" [paginator]="true">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('growth.agentWorkflow.columns.type') }}</th>
              <th>{{ t('growth.agentWorkflow.columns.fallback') }}</th>
              <th>{{ t('growth.agentWorkflow.columns.process') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.recommendationType }}</td>
              <td>{{ row.fallbackBehavior }}</td>
              <td>{{ row.workflowProcessDefinitionId ?? t('growth.agentWorkflow.deferredProcess') }}</td>
            </tr>
          </ng-template>
        </p-table>
      </section>

      <p-dialog
        [header]="t('common.add')"
        [visible]="dialogVisible()"
        (visibleChange)="dialogVisible.set($event)"
        [modal]="true"
        [style]="{ width: 'min(560px, 92vw)' }">
        <ain-info-hint [titleKey]="'growth.agentWorkflow.processDeferred.title'" [descriptionKey]="'growth.agentWorkflow.processDeferred.description'" />
        <ain-form-field [label]="t('growth.agentWorkflow.fields.recommendationType')">
          <p-select [options]="typeOptions" [(ngModel)]="model.recommendationType" optionLabel="label" optionValue="value" appendTo="body" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.agentWorkflow.fields.fallback')">
          <p-select [options]="fallbackOptions" [(ngModel)]="model.fallbackBehavior" optionLabel="label" optionValue="value" appendTo="body" />
        </ain-form-field>
        <ng-template pTemplate="footer">
          <div class="dialog-footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="dialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
          </div>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .card { border:1px solid #e5e7eb; border-radius:12px; padding:12px; }
    .toolbar { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:12px; }
    .dialog-footer { display:flex; justify-content:flex-end; gap:8px; flex-wrap:wrap; }
  `]
})
export class GrowthAgentWorkflowBindingsComponent implements OnInit {
  private readonly service = inject(GrowthAgentService);
  private readonly i18n = inject(LocalizationService);
  readonly items = signal<GrowthAgentWorkflowBinding[]>([]);
  readonly dialogVisible = signal(false);
  model = { recommendationType: 'CampaignBrief', fallbackBehavior: 'Block', isActive: true, configurationJson: '{}' };
  typeOptions = ['CampaignBrief','SegmentChange','ContentIdea','RevenueAction','RiskWarning','StrategyChange','OptimizerSuggestion'].map(v => ({ label: v, value: v }));
  fallbackOptions = [{ label: 'Block', value: 'Block' }, { label: 'PendingManualConfig', value: 'PendingManualConfig' }, { label: 'DirectAllowForNonSensitive', value: 'DirectAllowForNonSensitive' }];
  t(key: string): string { return this.i18n.translate(key); }
  ngOnInit(): void { this.load(); }
  load(): void { this.service.listWorkflowBindings().subscribe(items => this.items.set(items)); }
  openDialog(): void {
    this.model = { recommendationType: 'CampaignBrief', fallbackBehavior: 'Block', isActive: true, configurationJson: '{}' };
    this.dialogVisible.set(true);
  }
  save(): void {
    this.service.upsertWorkflowBinding({ ...this.model, workflowProcessDefinitionId: null }).subscribe({
      next: () => { this.dialogVisible.set(false); this.load(); }
    });
  }
}
